/**
 * ChildPicker - Reusable multi-select chip component for child tagging.
 */
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

interface ChildItem {
  id: string;
  name: string;
  birthDate: number;
  age: string;
  photoCount: number;
}

interface ChildPickerProps {
  familyId: string;
  selectedIds: string[];
  onToggle: (childId: string) => void;
}

export function ChildPicker({ familyId, selectedIds, onToggle }: ChildPickerProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['children', familyId],
    queryFn: () => apiClient.get<{ children: ChildItem[] }>(`/api/families/${familyId}/children`),
    enabled: !!familyId,
  });

  const children = data?.children ?? [];

  if (isLoading || children.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {children.map((child) => {
        const selected = selectedIds.includes(child.id);
        return (
          <button
            key={child.id}
            type="button"
            onClick={() => onToggle(child.id)}
            className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-all duration-200 ${
              selected
                ? 'bg-primary-600 text-white shadow-sm'
                : 'bg-muted text-muted-foreground hover:bg-accent'
            }`}
          >
            {child.name}
          </button>
        );
      })}
    </div>
  );
}
